import * as z from "zod";
import { flag, pagination } from "../index";
import { campaignEngagementDto } from "./insights";
import {
  decideCampaignInvitationInput,
  invitationFunnelStages,
  inviteToCampaignInput,
  issueSellerBookingLinkInput,
  type LinkFunnelStep,
} from "./source/campaign-funnel";

// The campaigns of Guestlist (ADR-299, ADR-287 §5): read from the projection of crm (ADR-292), the campaigns of booking
// with their invitations and the contacts of the invited customers. The writes are booking's (ADR-291): crm hands them
// through its port with the Actor of the person, with the inputs of the port, so their fields are those here.

/**
 * What Guestlist and its gateway need of the port, which only crm and the domains that serve it import (ADR-287 §2.2):
 * the permission of the writes, the stages, the answers of booking crm hands on, and their codes.
 */
export {
  type CampaignFunnelErrorCode,
  campaignInvitationsDto,
  decisionsAt,
  INVITATION_WRITE,
  type InvitationDecision,
  type InvitationFunnelStage,
  invitationFunnelStages,
  sellerBookingLinkDto,
} from "./source/campaign-funnel";

const campaignId = z.uuid();

/** How many invitations of a campaign, or of one of its brands, stand at each stage of the funnel. */
export const invitationStageCountsDto = z.object({
  invited: z.number().int(),
  booked: z.number().int(),
  visited: z.number().int(),
  no_show: z.number().int(),
  declined: z.number().int(),
  withdrawn: z.number().int(),
}) satisfies z.ZodType<Record<(typeof invitationFunnelStages)[number], number>>;
export type InvitationStageCounts = z.output<typeof invitationStageCountsDto>;

/** The campaigns, the latest to start first; the archived ones apart. */
export const listCrmCampaignsInput = z.object({
  /** Part of the name, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  archived: flag.optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCrmCampaignsInput = z.input<typeof listCrmCampaignsInput>;

export const crmCampaignSummaryDto = z
  .object({
    id: campaignId,
    name: z.string(),
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    /** The customers with at least an invitation. */
    customers: z.number().int(),
    stages: invitationStageCountsDto,
  })
  .meta({ id: "CrmCampaignSummary" });
export type CrmCampaignSummaryDto = z.output<typeof crmCampaignSummaryDto>;

export const getCrmCampaignInput = z.object({ campaignId });
export type GetCrmCampaignInput = z.input<typeof getCrmCampaignInput>;

/**
 * The steps of the personal links the matrix filters by (ADR-313 §4): the customers whose link went at least as far as
 * the page opened (`opened`) or the times of a day seen (`slots_viewed`).
 */
export const linkFilterSteps = ["opened", "slots_viewed"] as const satisfies readonly LinkFunnelStep[];
export type LinkFilterStep = (typeof linkFilterSteps)[number];

/**
 * How many invited customers of a campaign a link of theirs took at least as far as each step: a customer counts once,
 * whichever of its contacts opened it.
 */
export const linkStepCountsDto = z.object({
  opened: z.number().int(),
  slots_viewed: z.number().int(),
}) satisfies z.ZodType<Record<LinkFilterStep, number>>;
export type LinkStepCounts = z.output<typeof linkStepCountsDto>;

/**
 * A campaign with the brands its invitations are for, each with its stages: the columns of the matrix; and how far the
 * personal links of its customers went.
 */
export const crmCampaignDto = crmCampaignSummaryDto
  .extend({
    brands: z.array(z.object({ id: z.uuid(), name: z.string(), stages: invitationStageCountsDto })),
    links: linkStepCountsDto,
    /** The extended funnel, from the email to the order (plan §9.1). */
    engagement: campaignEngagementDto,
  })
  .meta({ id: "CrmCampaign" });
export type CrmCampaignDto = z.output<typeof crmCampaignDto>;

/**
 * The filters of the matrix and of its contacts: the customers with an invitation at that stage, or for that brand, or
 * whose personal link went at least as far as that step.
 */
const invitationFilter = {
  stage: z.enum(invitationFunnelStages).optional(),
  brandOrgId: z.uuid().optional(),
  linkStep: z.enum(linkFilterSteps).optional(),
};

/**
 * The rows of the matrix: the invited customers by name, each with every invitation it has in the campaign. With a
 * stage or a brand, the customers that have such an invitation.
 */
export const listCampaignInvitationsInput = z.object({
  campaignId,
  /** Part of the name of the customer, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  ...invitationFilter,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListCampaignInvitationsInput = z.input<typeof listCampaignInvitationsInput>;
export const campaignInvitationsQuery = listCampaignInvitationsInput.omit({ campaignId: true });

/** A cell of the matrix: the invitation of the customer to meet a brand. */
export const campaignInvitationDto = z
  .object({
    invitationId: z.uuid(),
    brandOrgId: z.uuid(),
    stage: z.enum(invitationFunnelStages),
    /** Null for an invitation born from an appointment nobody invited to. */
    invitedAt: z.iso.datetime().nullable(),
    appointmentStartsAt: z.iso.datetime().nullable(),
    stageChangedAt: z.iso.datetime().nullable(),
    /** What a decision on it sends back. */
    version: z.number().int(),
  })
  .meta({ id: "CampaignInvitation" });
export type CampaignInvitationDto = z.output<typeof campaignInvitationDto>;

export const campaignCustomerDto = z
  .object({
    customer: z.object({
      id: z.uuid(),
      /** Empty while the projection does not have the account yet. */
      name: z.string(),
      countryCode: z.string().nullable(),
      customerCode: z.string().nullable(),
    }),
    invitations: z.array(campaignInvitationDto),
  })
  .meta({ id: "CampaignCustomer" });
export type CampaignCustomerDto = z.output<typeof campaignCustomerDto>;

/**
 * The people of the invited customers, to send them their booking link: by customer, then by name. With a stage or a
 * brand, those of the customers that have such an invitation (e.g. still `invited`: not booked yet).
 */
export const listCampaignContactsInput = z.object({
  campaignId,
  /** Part of the name or of the email, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  ...invitationFilter,
  ...pagination({ defaultSize: 200, maxSize: 500 }),
});
export type ListCampaignContactsInput = z.input<typeof listCampaignContactsInput>;
export const campaignContactsQuery = listCampaignContactsInput.omit({ campaignId: true });

/**
 * What the dialog of the links shows of a person (Q-SEC-41): who and how to reach them; the role and the job come with
 * the card of an account (P8.14l-c2).
 */
export const campaignContactDto = z
  .object({
    id: z.uuid(),
    customerOrgId: z.uuid(),
    customerName: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** Null: booking sends it no link (`no_email`). */
    email: z.string().nullable(),
  })
  .meta({ id: "CampaignContact" });
export type CampaignContactDto = z.output<typeof campaignContactDto>;

/** The writes, on the path of their campaign or invitation: the rest of the input of the port is the body. */
export const inviteToCampaignBody = inviteToCampaignInput.omit({ campaignId: true });
export const issueSellerBookingLinkBody = issueSellerBookingLinkInput.omit({ campaignId: true });
export const campaignInvitationParams = decideCampaignInvitationInput.pick({ invitationId: true });
export const decideCampaignInvitationBody = decideCampaignInvitationInput.omit({ invitationId: true });
